import { useState } from 'react';
import type { ReactNode } from 'react';
import PositionSizeCalculator, { POSITION_ACCENT } from './PositionSizeCalculator';
import MarginCalculator, { MARGIN_ACCENT } from './MarginCalculator';
import ProfitLossCalculator, { PNL_ACCENT } from './ProfitLossCalculator';
import LiveTicker from './LiveTicker';

type Tool = {
  id: string;
  icon: string;
  accent: string;
  badge?: string;
  titleVi: string;
  titleEn: string;
  desc: string;
  tip: string;
  body: ReactNode;
};

const TOOLS: Tool[] = [
  {
    id: 'position',
    icon: 'ri-crosshair-2-line',
    accent: POSITION_ACCENT,
    badge: '★ Khuyên dùng',
    titleVi: 'Máy tính vị thế',
    titleEn: 'Position Size Calculator',
    desc: 'Xác định chính xác khối lượng lệnh dựa trên số vốn, tỷ lệ rủi ro và khoảng cách cắt lỗ — để không cảm xúc nào quyết định thay bạn.',
    tip: 'Đặt lệnh cắt lỗ ngay dưới mức hỗ trợ gần nhất để tránh bị quét vị thế.',
    body: <PositionSizeCalculator />,
  },
  {
    id: 'margin',
    icon: 'ri-bank-line',
    accent: MARGIN_ACCENT,
    titleVi: 'Máy tính ký quỹ',
    titleEn: 'Margin Calculator',
    desc: 'Tính ngay số tiền ký quỹ cần thiết để mở một vị thế, theo cặp tiền, khối lượng và mức đòn bẩy bạn chọn.',
    tip: 'Đòn bẩy càng cao, ký quỹ càng thấp nhưng rủi ro bị thanh lý cũng càng lớn.',
    body: <MarginCalculator />,
  },
  {
    id: 'pnl',
    icon: 'ri-line-chart-line',
    accent: PNL_ACCENT,
    titleVi: 'Máy tính Lãi / Lỗ',
    titleEn: 'Profit / Loss Calculator',
    desc: 'Ước lượng lợi nhuận hoặc thua lỗ khi giá biến động, dựa trên giá vào lệnh, giá đóng lệnh và khối lượng.',
    tip: 'Chốt lời từng phần giúp bảo vệ lợi nhuận khi thị trường đột ngột đảo chiều.',
    body: <ProfitLossCalculator />,
  },
];

export default function CalculatorWorkspace() {
  const [activeId, setActiveId] = useState('position');
  const active = TOOLS.find((t) => t.id === activeId) ?? TOOLS[0];

  return (
    <div className="mt-12 md:mt-16">
      <div className="rounded-[28px] border border-violet-200 bg-gradient-to-b from-violet-50/70 to-white p-1.5">
        <div className="relative overflow-hidden rounded-[22px] bg-white">
          {/* subtle grid background */}
          <div className="terminal-grid pointer-events-none absolute inset-0" />

          {/* ambient violet glows */}
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-violet-100/70 blur-3xl" />
            <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-violet-100/50 blur-3xl" />
          </div>

          <div className="relative">
            {/* terminal header bar */}
            <div className="flex items-center gap-2 border-b border-violet-100 px-5 py-3">
              <span className="h-3 w-3 rounded-full bg-rose-400/80" />
              <span className="h-3 w-3 rounded-full bg-amber-400/80" />
              <span className="h-3 w-3 rounded-full bg-emerald-400/80" />
              <span className="ml-2 font-mono text-xs text-slate-400">forex-tools</span>
              <span className="ml-auto flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span className="font-mono text-[10px] font-semibold tracking-widest text-emerald-600">LIVE</span>
              </span>
            </div>

            {/* live price ticker */}
            <LiveTicker />

            {/* body */}
            <div className="grid md:grid-cols-[260px_1fr]">
              <aside className="flex flex-col border-b border-violet-100 p-3 md:border-b-0 md:border-r">
                <div className="flex flex-col gap-1.5">
                  {TOOLS.map((tool) => {
                    const isActive = tool.id === activeId;
                    return (
                      <button
                        key={tool.id}
                        type="button"
                        onClick={() => setActiveId(tool.id)}
                        className={`group relative flex w-full items-start gap-3 rounded-2xl p-3.5 text-left transition-all duration-200 ${
                          isActive ? '' : 'hover:bg-violet-50/60'
                        }`}
                      >
                        {isActive && (
                          <span
                            className="absolute inset-0 rounded-2xl"
                            style={{
                              background: `linear-gradient(135deg, ${tool.accent}14, transparent 70%)`,
                              border: `1px solid ${tool.accent}2b`,
                            }}
                          />
                        )}
                        <span
                          className={`absolute left-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-full transition-opacity ${
                            isActive ? 'opacity-100' : 'opacity-0'
                          }`}
                          style={{
                            backgroundColor: tool.accent,
                            boxShadow: isActive ? `0 0 10px ${tool.accent}66` : undefined,
                          }}
                        />
                        <span
                          className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-all ${
                            isActive ? 'text-white' : 'bg-violet-50 text-slate-400 group-hover:text-violet-500'
                          }`}
                          style={isActive ? { backgroundColor: tool.accent } : undefined}
                        >
                          <i className={`${tool.icon} text-lg`} />
                        </span>
                        <span className="relative min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span
                              className={`truncate font-display text-sm font-bold ${
                                isActive ? 'text-slate-900' : 'text-slate-600'
                              }`}
                            >
                              {tool.titleVi}
                            </span>
                            {tool.badge && (
                              <span
                                className="shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[9px] font-bold"
                                style={{ backgroundColor: `${tool.accent}1a`, color: tool.accent }}
                              >
                                {tool.badge}
                              </span>
                            )}
                          </span>
                          <span className="mt-0.5 block truncate font-mono text-[10px] uppercase tracking-wide text-slate-400">
                            {tool.titleEn}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* sidebar footer fills the empty space below tools */}
                <div className="mt-auto flex flex-col gap-3 pt-4">
                  {/* quick tip */}
                  <div className="relative overflow-hidden rounded-2xl border border-violet-100 bg-violet-50/50 p-4">
                    <div
                      className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full blur-2xl"
                      style={{ background: `${active.accent}2b` }}
                    />
                    <span
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-white"
                      style={{ backgroundColor: active.accent }}
                    >
                      <i className="ri-lightbulb-flash-line text-base" />
                    </span>
                    <p className="mt-3 font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                      Mẹo nhanh
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{active.tip}</p>
                  </div>

                  {/* market sessions */}
                  <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-4">
                    <p className="font-mono text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                      Phiên giao dịch
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {[
                        { label: 'Sydney', status: 'open' },
                        { label: 'Tokyo', status: 'open' },
                        { label: 'London', status: 'closed' },
                        { label: 'New York', status: 'closed' },
                      ].map((s) => (
                        <div
                          key={s.label}
                          className="flex items-center gap-2 rounded-lg border border-violet-50 bg-white px-2.5 py-2"
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              s.status === 'open' ? 'bg-emerald-500' : 'bg-slate-300'
                            }`}
                          />
                          <span
                            className={`text-[11px] font-medium ${
                              s.status === 'open' ? 'text-slate-700' : 'text-slate-400'
                            }`}
                          >
                            {s.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* version + disclaimer */}
                  <div className="flex flex-col gap-2 px-1 pb-1">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] text-slate-400">forex-tools v2.4</span>
                      <span className="flex items-center gap-1.5 font-mono text-[10px] text-emerald-600">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                        đã kết nối
                      </span>
                    </div>
                    <p className="text-[10px] leading-relaxed text-slate-400">
                      Công cụ chỉ mang tính tham khảo, không cấu thành lời khuyên đầu tư.
                    </p>
                  </div>
                </div>
              </aside>

              <div key={activeId} className="animate-fade-up p-5 md:p-7">
                <div className="flex flex-wrap items-center gap-3 border-b border-violet-100 pb-5">
                  <span
                    className="flex h-11 w-11 items-center justify-center rounded-xl text-white"
                    style={{ backgroundColor: active.accent, boxShadow: `0 2px 14px ${active.accent}44` }}
                  >
                    <i className={`${active.icon} text-xl`} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-xl font-bold text-slate-900">{active.titleVi}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{active.titleEn}</p>
                  </div>
                  {active.badge && (
                    <span
                      className="whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold"
                      style={{ backgroundColor: `${active.accent}1a`, color: active.accent }}
                    >
                      {active.badge}
                    </span>
                  )}
                </div>

                <p className="mt-4 text-sm leading-relaxed text-slate-600">{active.desc}</p>

                <div className="mt-6">{active.body}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}